import { isTeamInviteMinorAge, PARENTAL_CONSENT_ROSTER_HIDE_DAYS } from '../config/agePolicy';

/**
 * Shared gate for team_roster_entries consent state (GLY-363).
 *
 * A missing consent_given_at can mean either:
 * - the athlete hasn't accepted their team invite yet (any age), or
 * - a parent/guardian withdrew consent for a minor, or
 * - the athlete is a minor whose parent/guardian has never consented.
 *
 * Coaches should only be hard-blocked (badge + navigation + journey/profile access) for the
 * parental-consent cases. Adults with a simply-unaccepted invite are not blocked here.
 */
export interface RosterConsentGateInput {
  consentGivenAt?: string | null;
  consentWithdrawnAt?: string | null;
  dateOfBirth?: string | null;
}

/**
 * True when a parent/guardian must (re-)consent before a coach can map or view this rower.
 * Unknown date of birth never trips the minor check (avoids false-flagging adults).
 */
export function isAwaitingParentalConsent({
  consentGivenAt,
  dateOfBirth,
}: RosterConsentGateInput): boolean {
  if (consentGivenAt) return false;
  if (!dateOfBirth || !isTeamInviteMinorAge(dateOfBirth)) return false;
  return true;
}

export const PARENTAL_CONSENT_BLOCKED_MESSAGE =
  'Parent or guardian consent is required before you can view this athlete.';

export interface RosterHideAfterWithdrawInput {
  consentWithdrawnAt?: string | null;
  /** Injectable for tests; defaults to the current time. */
  now?: Date;
}

/**
 * True once PARENTAL_CONSENT_ROSTER_HIDE_DAYS have elapsed since a parent/guardian
 * withdrew consent (GLY-363). Coaches see the athlete on the roster (badged, blocked)
 * for a grace period, then the athlete drops off the roster list entirely.
 */
export interface RosterHideAfterWithdrawWithAgeInput extends RosterHideAfterWithdrawInput {
  /** When provided, adults are never hidden for parental-withdrawal aging (GLY-365). */
  dateOfBirth?: string | null;
}

export function isHiddenFromCoachRosterAfterConsentWithdraw({
  consentWithdrawnAt,
  dateOfBirth,
  now = new Date(),
}: RosterHideAfterWithdrawWithAgeInput): boolean {
  if (!consentWithdrawnAt) return false;
  // Adults are not subject to parental-consent roster hide (GLY-365).
  if (dateOfBirth && !isTeamInviteMinorAge(dateOfBirth)) return false;
  const withdrawnAt = new Date(consentWithdrawnAt);
  if (Number.isNaN(withdrawnAt.getTime())) return false;
  const elapsedMs = now.getTime() - withdrawnAt.getTime();
  const hideThresholdMs = PARENTAL_CONSENT_ROSTER_HIDE_DAYS * 24 * 60 * 60 * 1000;
  return elapsedMs >= hideThresholdMs;
}
